/**
 * Block Analyzer — embedded block map.
 *
 * Mounts the Mapbox map shown at the top of the block analyzer lot-detail
 * tab. The map renders:
 *
 *   - All non-historic lots on the block as colored fills (under-built %
 *     color ramp) with black 1px lot borders.
 *   - An orange outline on lots that have dev-rights ACRIS docs.
 *   - A dot-pattern overlay (via deck.gl `MapboxOverlay` in interleaved
 *     mode) on currently-selected lots, kept in sync with the shared
 *     selection store (`window.PsBlockAnalyzerSelection`).
 *   - A star marker on the current lot.
 *   - A custom hover treatment (darker fill + cursor:pointer + small
 *     address tooltip) driven by feature-state on the hosted style's
 *     `lot-fills` source. We disable the base map's built-in `lot-fills`
 *     hover so the two systems don't fight; the base map's hover-label
 *     DOM/content is reused for the tooltip.
 *
 * Mount lifecycle is managed by `window.PsEmbeddedMaps` — the container
 * (`#block_analyzer_map`) is re-created on every tab load, so this
 * module is effectively stateless: each `mountMap()` call wires a fresh
 * map against the fresh DOM.
 *
 * Exposed as `window.PsBlockAnalyzer.mountMap()`. Called from the
 * lot-detail flow (see `_detail.html.erb`) on tab activation and on
 * AJAX content load.
 *
 * Globals consumed (all loaded via the asset pipeline, not Vite):
 *   - `mapboxgl`, `mb_map_styles`, `mb_activate_map`   (mapbox setup)
 *   - `_` (lodash)
 *   - `window.PsEmbeddedMaps`                          (mount lifecycle)
 *   - `window.PsBlockAnalyzerSelection`                (selection store)
 *   - `window.PsDeckMaps`, `window.deck`               (deck.gl overlay)
 *   - `window.PS_DEBUG_BLOCK_MAP`                      (debug flag)
 */

function mountMap() {
  let map_el = document.getElementById('block_analyzer_map');
  if (!map_el || !window.PsEmbeddedMaps) { return; }

  let bbl = map_el.dataset.bbl;
  let center = JSON.parse(map_el.dataset.center);
  let bounds = JSON.parse(map_el.dataset.bounds);
  let block_lot_fill_geojson = JSON.parse(map_el.dataset.blockLotFillGeojson || '{"type":"FeatureCollection","features":[]}');

  // Shared lot-selection store (synced with block-analyzer grid). Initialized
  // here so the map can register an allowed-BBL set even before the grid
  // finishes mounting (in case the user clicks the map first).
  let selection_store = (window.PsBlockAnalyzerSelection &&
                         window.PsBlockAnalyzerSelection.getStoreFor(map_el)) || null;
  if (selection_store) {
    let allowed_bbls = (block_lot_fill_geojson.features || [])
      .map(function(f){ return f.properties && f.properties.bbl; })
      .filter(function(b){ return b != null; });
    selection_store.setAllowed(allowed_bbls);
  }

  window.PsEmbeddedMaps.mount({
    propertyId: bbl,
    tabName: 'block_analyzer',
    container: map_el,
    createMap: function(){
      let tab_map = new mapboxgl.Map({
        container: map_el,
        style: mb_map_styles.outlines,
        center: center,
        zoom: 16,
        minZoom: 10,
        maxZoom: 19
      });

      mb_activate_map(tab_map, {view:'outlines', lot_hover:'small'});
      tab_map.addControl(new mapboxgl.NavigationControl({showCompass:false,showZoom:true}),'top-left');

      // -----------------------------------------------------------
      // Custom lot hover (block analyzer map only).
      //
      // The hosted style's `lot-fills` layer has its own hover paint,
      // but on this map it sits underneath our colored
      // `block_analyzer_lot_fills` layer (fill-opacity 0.75) so its
      // hover shading is barely visible. We replace it with a more
      // pronounced overlay (`block_analyzer_lot_hover`, added inside
      // `add_block_lot_fills` below) and drive it ourselves.
      //
      // We disable the base map's `lot-fills` hover handling so the
      // two systems don't mix, and reuse the base map's existing
      // hover-label DOM/content for the address tooltip.
      // -----------------------------------------------------------
      tab_map.ps.hoverable_layers = _.without(tab_map.ps.hoverable_layers, 'lot-fills');

      // Hover shading is driven by feature-state on the vector-tile
      // `lot-fills` source (same mechanism the hosted style uses), NOT
      // by setFilter on our overlay layer. Feature-state updates are
      // shader-uniform changes (~instant); setFilter forces tile
      // re-evaluation and added a perceptible delay on first hover.
      let current_hover_feature = null;

      let set_hover_feature_state = function(feature, hovered){
        if (!feature) return;
        tab_map.setFeatureState(
          {source: feature.source, sourceLayer: feature.sourceLayer, id: feature.id},
          {block_analyzer_hover: hovered}
        );
      };

      let clear_block_hover = function(){
        if (current_hover_feature) {
          set_hover_feature_state(current_hover_feature, false);
          current_hover_feature = null;
        }
        tab_map.getCanvas().style.cursor = '';
        tab_map.ps.hide_hover_label();
      };

      tab_map.on('mousemove', function(e){
        // Pick the top lot-fills feature manually since we removed it
        // from hoverable_layers above.
        let features = tab_map.queryRenderedFeatures(e.point, {layers: ['lot-fills']});
        let feature = features && features[0];

        if (!feature || feature.id == null) {
          clear_block_hover();
          return;
        }

        // If we moved to a different feature, swap feature-state.
        let same_feature = current_hover_feature
          && current_hover_feature.id === feature.id
          && current_hover_feature.sourceLayer === feature.sourceLayer;

        if (!same_feature) {
          if (current_hover_feature) set_hover_feature_state(current_hover_feature, false);
          set_hover_feature_state(feature, true);
          current_hover_feature = feature;
        }

        // Cursor + hover label are written unconditionally on every move
        // so the base map's mousemove handler (which runs first and
        // unconditionally clears both when it sees no hoverable features
        // — we removed lot-fills from hoverable_layers) doesn't leave the
        // cursor reverted to default after the initial enter.
        tab_map.getCanvas().style.cursor = 'pointer';
        tab_map.ps.show_hover_label(e, tab_map.ps.get_hover_content(feature));
      });

      tab_map.on('mouseout', clear_block_hover);

      let debug_log = function(message, extra){
        if (!window.PS_DEBUG_BLOCK_MAP) { return; }
        console.log('[block-map]', message, extra || '');
      };

      tab_map.on('styledata', function(){
        debug_log('styledata', {styleLoaded: tab_map.isStyleLoaded(), hasFillLayer: !!tab_map.getLayer('block_analyzer_lot_fills')});
      });
      tab_map.on('sourcedata', function(e){
        if (e.sourceId == 'block_analyzer_lot_fills_source') {
          debug_log('sourcedata:block fills', {isSourceLoaded: e.isSourceLoaded});
        }
      });
      tab_map.on('movestart', function(){ debug_log('movestart'); });
      tab_map.on('moveend', function(){ debug_log('moveend'); });
      tab_map.on('zoomstart', function(){ debug_log('zoomstart'); });
      tab_map.on('zoomend', function(){ debug_log('zoomend'); });

      let set_selected_lot_marker = function(point){
        if (tab_map.block_analyzer_selected_marker) {
          tab_map.block_analyzer_selected_marker.remove();
          tab_map.block_analyzer_selected_marker = null;
        }

        let el = document.createElement('div');
        el.className = 'block_analyzer_selected_lot_marker';
        el.style.backgroundImage = 'url(https://pub.propertyscout.com/maps/markers/star80.png)';
        el.style.backgroundSize = 'contain';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.backgroundPosition = 'center';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.pointerEvents = 'none';

        tab_map.block_analyzer_selected_marker = new mapboxgl.Marker(el, {anchor:'center'})
          .setLngLat(point)
          .addTo(tab_map);
      };

      // Layer id used as `beforeId` when adding OUR OWN block-analyzer
      // fills, strokes, and hover overlay. Points at the hosted style's
      // top-most lot-label layer so labels stay readable on top of our
      // color overlays.
      let find_block_analyzer_before_id = function(){
        let layers = (tab_map.getStyle() || {}).layers || [];
        let preferred_layer_ids = ['lot-labels copy', 'lot-labels', 'block-labels'];

        for (let i = 0; i < preferred_layer_ids.length; i++) {
          if (layers.find(function(layer){ return layer.id == preferred_layer_ids[i]; })) {
            return preferred_layer_ids[i];
          }
        }

        let first_symbol_layer = layers.find(function(layer){ return layer.type == 'symbol'; });
        return first_symbol_layer ? first_symbol_layer.id : undefined;
      };

      // Layer id used as `beforeId` for the deck.gl dot-pattern overlay.
      // The dot pattern is a FILL effect, so we want it under all of
      // our stroke layers — not just under the labels. Prefers our own
      // top-most stroke layer (`block_analyzer_dev_rights_docs_lines`,
      // then `block_analyzer_lot_lines`), falling back to the label
      // layer if those haven't been added yet (e.g. first map mount
      // before refresh_highlights runs).
      let find_deck_before_id = function(){
        let preferred_layer_ids = [
          'block_analyzer_dev_rights_docs_lines',
          'block_analyzer_lot_lines'
        ];
        for (let i = 0; i < preferred_layer_ids.length; i++) {
          if (tab_map.getLayer(preferred_layer_ids[i])) {
            return preferred_layer_ids[i];
          }
        }
        return find_block_analyzer_before_id();
      };

      let selected_lot_pattern_feature_collection = function(){
        let selected_bbls = selection_store ? selection_store.getSelected() : [];
        if (!selected_bbls.length) {
          return {type: 'FeatureCollection', features: []};
        }
        let selected_set = new Set(selected_bbls.map(String));
        let selected_features = (block_lot_fill_geojson.features || []).filter(function(feature){
          return feature.properties && selected_set.has(String(feature.properties.bbl));
        });
        return {type: 'FeatureCollection', features: selected_features};
      };

      let apply_lot_label_halo = function(){
        ['lot-labels copy', 'lot-labels'].forEach(function(layer_id){
          if (!tab_map.getLayer(layer_id)) { return; }

          tab_map.setPaintProperty(layer_id, 'text-halo-color', '#fff');
          tab_map.setPaintProperty(layer_id, 'text-halo-width', 1.5);
          tab_map.setPaintProperty(layer_id, 'text-halo-blur', 0.5);
        });
      };

      // -----------------------------------------------------------
      // Selected-lot dot-pattern overlay (deck.gl).
      //
      // We keep ONE deck.gl `GeoJsonLayer` mounted across selection
      // changes. On each store update we only swap its `data` prop;
      // deck.gl's prop-diff (keyed by layer `id`) then re-uploads
      // geometry buffers without touching the shader, texture, or
      // extension state.
      //
      // The previous implementation called PsDeckMaps.createPatternedLayer
      // on every selection toggle, which (a) generated a fresh 512×512
      // dot-pattern canvas, (b) uploaded it as a brand-new GPU texture
      // with full mipmap chain, and (c) built a new FillStyleExtension
      // instance — all on the synchronous click → store → subscriber
      // path. That added a half-second of perceived lag per toggle.
      //
      // Cached state lives on the overlay itself so it has the same
      // lifetime as the overlay's luma.gl device (a fresh map mount
      // creates a fresh overlay → fresh device → fresh cache).
      // -----------------------------------------------------------
      let build_selected_lot_pattern_layer = function(overlayInstance, device, data, beforeId){
        // Ensure the #7326 shader patch is installed before any
        // FillStyleExtension layer compiles. Idempotent.
        if (window.PsDeckMaps && window.PsDeckMaps.installNoModInject) {
          window.PsDeckMaps.installNoModInject();
        }

        let cache = overlayInstance._psPatternLayerCache;
        if (!cache || cache.device !== device) {
          // First call for this overlay's device — build the heavy bits
          // once and stash them. Subsequent calls reuse everything
          // except `data`.
          let pattern_canvas = window.PsDeckMaps.PATTERN_PRESETS.dense_dots();
          let texture        = window.PsDeckMaps.makeMippedTexture(device, pattern_canvas);
          let frameSize      = texture.width;
          let tileWorldMeters = 24;

          cache = {
            device: device,
            texture: texture,
            extension: new window.deck.FillStyleExtension({pattern: true}),
            // Stable references for accessor / config props — reusing
            // the same closures and array literals lets deck.gl's
            // shallow-equal prop diff classify them as unchanged.
            staticProps: {
              id: 'block_analyzer_selected_lot_pattern',
              pickable: false,
              stroked: true,
              filled: true,
              lineWidthMinPixels: 0,
              getLineColor: [0, 0, 0, 0],
              getFillColor: [0, 0, 0, 128],
              fillPatternMask: true,
              fillPatternMapping: {full: {x: 0, y: 0, width: frameSize, height: frameSize}},
              getFillPattern: function(){ return 'full'; },
              getFillPatternScale: tileWorldMeters / frameSize,
              getFillPatternOffset: [0, 0]
            }
          };
          overlayInstance._psPatternLayerCache = cache;
          debug_log('pattern layer cache initialized', {textureSize: frameSize});
        }

        return new window.deck.GeoJsonLayer(Object.assign({}, cache.staticProps, {
          data: data,
          fillPatternAtlas: cache.texture,
          extensions: [cache.extension],
          beforeId: beforeId
        }));
      };

      // Rebuild (or initialize) the deck.gl dot-pattern overlay so it
      // reflects the current selection from `selection_store`. Safe to call
      // many times — it sets up the MapboxOverlay control on first call
      // and only updates its layers thereafter.
      //
      // IMPORTANT: do not run before the map style is loaded — otherwise
      // `find_deck_before_id()` returns garbage and the dot layer can end
      // up underneath opaque mapbox fills (invisible). Calls made before
      // load are no-ops; `refresh_highlights` (on `load`) will pick up
      // the latest selection from the store, and store changes after
      // that point will refresh normally.
      let refresh_selected_lot_pattern_overlay = function(attempt){
        attempt = attempt || 0;

        if (_.isEmpty(block_lot_fill_geojson.features)) { return; }
        if (tab_map._removed) { return; }
        if (!tab_map.isStyleLoaded()) {
          // Style not ready yet: try again once it is. Avoid stacking up
          // duplicate listeners by using `once`.
          debug_log('defer selected lot pattern: style not loaded yet');
          tab_map.once('idle', function(){ refresh_selected_lot_pattern_overlay(); });
          return;
        }

        if (!(window.PsDeckMaps && window.deck && window.deck.MapboxOverlay)) {
          if (attempt < 100) {
            return setTimeout(function(){ refresh_selected_lot_pattern_overlay(attempt + 1); }, 30);
          }
          debug_log('skip selected lot pattern: PsDeckMaps not ready');
          return;
        }

        let overlay = tab_map.block_analyzer_selected_lot_pattern_overlay;
        if (!overlay) {
          overlay = new window.deck.MapboxOverlay({
            interleaved: true,
            layers: [],
            // Cursor handling: in interleaved mode deck.gl writes its
            // getCursor() result to the SAME canvas element that mapbox
            // uses for hover cursors, on every render frame. If we return
            // anything other than the canvas's current cursor here, deck
            // will stomp the 'pointer' that base_map's mousemove handler
            // just set, and lots will never appear hoverable.
            //
            // Returning the current canvas cursor makes deck's write a
            // no-op, leaving mapbox's hover logic in full control.
            getCursor: function(){
              let canvas = tab_map.getCanvas();
              return (canvas && canvas.style.cursor) || '';
            }
          });
          tab_map.block_analyzer_selected_lot_pattern_overlay = overlay;
          tab_map.addControl(overlay);
        }

        // Captured by apply_layers below so we can report the gap
        // between scheduling rAF and the callback actually firing
        // (i.e. how long the browser took to give us a frame).
        let raf_scheduled_at = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;

        let apply_layers = function(device_attempt){
          device_attempt = device_attempt || 0;
          let apply_start = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;
          if (tab_map._removed) { return; }

          let device = overlay._deck && overlay._deck.device;
          if (!device) {
            debug_log('apply_layers: device not ready yet', {device_attempt: device_attempt});
            if (device_attempt < 30) {
              return requestAnimationFrame(function(){ apply_layers(device_attempt + 1); });
            }
          debug_log('skip selected lot pattern: deck device not ready');
            return;
          }

          let selected_lot_geojson = selected_lot_pattern_feature_collection();
          // Recompute `before_id` on every refresh — the layer ordering
          // can shift (e.g. our own block-fill layers were just added),
          // and caching it once led to bad placements in some races.
          let before_id = find_deck_before_id();

          // Update paths below split into two cases:
          //   - FIRST CALL  (per overlay lifetime): goes through
          //     overlay.setProps() so MapboxOverlay registers its
          //     MapboxLayerGroup wrapper into Mapbox's style stack at
          //     the requested beforeId position. This is the only call
          //     that pays the Mapbox style-mutation + label-placement
          //     cost. It happens once per map mount.
          //   - SUBSEQUENT CALLS: bypass overlay.setProps and push the
          //     new layer set directly into deck.gl via
          //     overlay._deck.setProps, then nudge Mapbox to repaint via
          //     tab_map.triggerRepaint. The previously-registered
          //     MapboxLayerGroup stays slotted at the same z-position;
          //     Mapbox's repaint hits MapboxLayerGroup.render() which
          //     calls drawLayerGroup() which queries deck's current
          //     layers prop with a beforeId filter — so z-order is
          //     preserved without any Mapbox-style mutation. This is
          //     the hot path that avoids the ~100-800ms label re-place
          //     pass that was the click-to-paint bottleneck.
          //
          // Reference (deck.gl 9.x sources, mirrored under node_modules):
          //   @deck.gl/mapbox/src/mapbox-overlay.ts   setProps() entry
          //   @deck.gl/mapbox/src/resolve-layer-groups.ts  group lifecycle
          //   @deck.gl/mapbox/src/mapbox-layer-group.ts    render() hook
          //   @deck.gl/mapbox/src/deck-utils.ts            drawLayerGroup()

          let layer = null;
          let t0 = 0, t1 = 0, t2 = 0;

          if (_.isEmpty(selected_lot_geojson.features)) {
            // Empty selection.
            if (!overlay._psFirstCallDone) {
              // First call ever, and there's nothing to render. Skip
              // entirely — we'll do the first-call registration on the
              // next non-empty toggle. Leaves the overlay control
              // mounted but with no layers, which is harmless.
              debug_log('selected lot pattern overlay cleared (pre-init)', {
                rafGapMs: window.PS_DEBUG_BLOCK_MAP ? +(apply_start - raf_scheduled_at).toFixed(2) : null
              });
              return;
            }
            // Hot path — just clear deck's layer set and trigger a
            // repaint. The MapboxLayerGroup stays registered so the
            // next non-empty toggle can hit the hot path too.
            overlay._deck.setProps({layers: []});
            tab_map.triggerRepaint();
            debug_log('selected lot pattern overlay cleared', {
              rafGapMs: window.PS_DEBUG_BLOCK_MAP ? +(apply_start - raf_scheduled_at).toFixed(2) : null
            });
            return;
          }

          t0 = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;
          layer = build_selected_lot_pattern_layer(overlay, device, selected_lot_geojson, before_id);
          t1 = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;

          if (!overlay._psFirstCallDone) {
            // Cold path — register the MapboxLayerGroup wrapper with
            // Mapbox at the correct beforeId. This is the call that
            // pays the Mapbox-side cost; it only fires once.
            overlay.setProps({layers: [layer]});
            overlay._psFirstCallDone = true;
          } else {
            // Hot path — bypass MapboxOverlay's setProps. See the big
            // comment block above for the why.
            overlay._deck.setProps({layers: [layer]});
            tab_map.triggerRepaint();
          }
          t2 = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;

          debug_log('selected lot pattern overlay refreshed', {
            features: selected_lot_geojson.features.length,
            beforeId: before_id,
            rafGapMs:      window.PS_DEBUG_BLOCK_MAP ? +(apply_start - raf_scheduled_at).toFixed(2) : null,
            deviceAttempt: device_attempt,
            buildLayerMs:  +(t1 - t0).toFixed(2),
            setPropsMs:    +(t2 - t1).toFixed(2)
          });

          // Also measure how long until the next paint actually lands.
          // This catches latency that happens AFTER setProps returns —
          // e.g. Mapbox's repaint scheduler, deck.gl's internal render
          // queue, or browser compositing.
          if (window.PS_DEBUG_BLOCK_MAP) {
            let post_setprops = performance.now();
            requestAnimationFrame(function(){
              debug_log('next frame after setProps', {
                msFromSetProps: +(performance.now() - post_setprops).toFixed(2)
              });
            });
          }
        };

        requestAnimationFrame(function(){ apply_layers(); });
      };

      let add_block_lot_fills = function(){
        if (_.isEmpty(block_lot_fill_geojson.features)) {
          debug_log('skip fills: no features');
          return;
        }

        let source = tab_map.getSource('block_analyzer_lot_fills_source');
        if (source) {
          debug_log('update existing fill source', {features: block_lot_fill_geojson.features.length});
          source.setData(block_lot_fill_geojson);
        } else {
          debug_log('add fill source', {features: block_lot_fill_geojson.features.length});
          tab_map.addSource('block_analyzer_lot_fills_source', {
            type: 'geojson',
            data: block_lot_fill_geojson
          });
        }

        // Insert all of our block-analyzer fill/line/hover layers BELOW
        // the hosted style's lot-label symbol layer so labels stay
        // readable on top of our color overlays. `find_deck_before_id`
        // returns the first available label layer id (with fallbacks)
        // and is reused by the deck.gl dot-pattern overlay below.
        //
        // Mapbox preserves insert-order between layers that share the
        // same `beforeId`, so the four addLayer calls below stack
        // bottom-to-top in source order. We add all FILL layers first,
        // then all STROKE/LINE layers, so strokes always render on
        // top of fills regardless of which features overlap.
        //
        // Full final stack (bottom → top):
        //   1. block_analyzer_lot_fills              (FAR color fill)
        //   2. block_analyzer_lot_hover              (hover darkening fill)
        //   3. deck.gl dot pattern for selected lots (treated as FILL;
        //      slotted under our strokes via find_deck_before_id())
        //   4. block_analyzer_lot_lines              (1px black borders)
        //   5. block_analyzer_dev_rights_docs_lines  (orange outline)
        //   6. lot labels (hosted style — always on top)
        let before_id = find_block_analyzer_before_id();

        if (!tab_map.getLayer('block_analyzer_lot_fills')) {
          debug_log('add fill layer', {beforeId: before_id});
          tab_map.addLayer({
            id: 'block_analyzer_lot_fills',
            type: 'fill',
            source: 'block_analyzer_lot_fills_source',
            layout: {},
            paint: {
              'fill-color': ['get', 'fill'],
              'fill-opacity': 0.75
            }
          }, before_id);
        } else {
          debug_log('fill layer already present');
        }

        if (!tab_map.getLayer('block_analyzer_lot_hover')) {
          // Sourced from the hosted style's `lot-fills` (vector tile) so
          // the dark hover treatment works uniformly on every NYC lot,
          // not just lots on the current block. Source/source-layer are
          // read off the live `lot-fills` layer because they live in
          // Mapbox Studio and aren't otherwise exposed to this code.
          //
          // Per-feature visibility is driven by feature-state
          // (`block_analyzer_hover`), set by the mousemove handler
          // wired up above. fill-opacity is 0 when the state is false
          // and 0.28 when true — effectively a per-feature toggle with
          // no tile re-evaluation cost.
          let lot_fills_layer = tab_map.getLayer('lot-fills');
          if (lot_fills_layer) {
            debug_log('add hover overlay layer', {beforeId: before_id});
            tab_map.addLayer({
              id: 'block_analyzer_lot_hover',
              type: 'fill',
              source: lot_fills_layer['source'],
              'source-layer': lot_fills_layer['source-layer'],
              layout: {},
              paint: {
                'fill-color': '#000',
                'fill-opacity': [
                  'case',
                  ['boolean', ['feature-state', 'block_analyzer_hover'], false],
                  0.28,
                  0
                ]
              }
            }, before_id);
          } else {
            debug_log('skip hover overlay layer: lot-fills not found in style');
          }
        } else {
          debug_log('hover overlay layer already present');
        }

        if (!tab_map.getLayer('block_analyzer_lot_lines')) {
          debug_log('add lot line layer', {beforeId: before_id});
          tab_map.addLayer({
            id: 'block_analyzer_lot_lines',
            type: 'line',
            source: 'block_analyzer_lot_fills_source',
            layout: {},
            paint: {
              'line-color': '#000',
              'line-width': 1,
              'line-opacity': 1
            }
          }, before_id);
        } else {
          debug_log('lot line layer already present');
        }

        if (!tab_map.getLayer('block_analyzer_dev_rights_docs_lines')) {
          debug_log('add dev-rights docs line layer', {beforeId: before_id});
          tab_map.addLayer({
            id: 'block_analyzer_dev_rights_docs_lines',
            type: 'line',
            source: 'block_analyzer_lot_fills_source',
            filter: ['>', ['get', 'dev_rights_docs_count'], 0],
            layout: {},
            paint: {
              'line-color': '#FF6600',
              'line-width': 2.4,
              'line-opacity': 1
            }
          }, before_id);
        } else {
          debug_log('dev-rights docs line layer already present');
        }

      };

      let refresh_highlights = function(){
        debug_log('refresh_highlights:start', {style: tab_map.getStyle().name, styleLoaded: tab_map.isStyleLoaded()});
        tab_map.fitBounds([[bounds[0],bounds[1]],[bounds[2],bounds[3]]], {padding:25, maxZoom:17, linear:true});
        add_block_lot_fills();
        apply_lot_label_halo();
        set_selected_lot_marker(center);
        refresh_selected_lot_pattern_overlay();
        debug_log('refresh_highlights:done', {hasFillLayer: !!tab_map.getLayer('block_analyzer_lot_fills')});
      };

      tab_map.on('load', refresh_highlights);

      // Sync selection: store → map (rebuild dot-pattern overlay).
      // Subscription auto-detaches when its closure becomes unreachable
      // after the container DOM is destroyed on bbl change.
      if (selection_store) {
        selection_store.subscribe(function(){
          let t0 = window.PS_DEBUG_BLOCK_MAP ? performance.now() : 0;
          refresh_selected_lot_pattern_overlay();
          if (window.PS_DEBUG_BLOCK_MAP) {
            debug_log('store subscriber (sync portion)', {ms: +(performance.now() - t0).toFixed(2)});
          }
        });
      }

      // Sync selection: map clicks → store. Only lots on the current block
      // (i.e. present in `block_lot_fill_geojson`) are allowed; clicks on
      // other lots are silently ignored. The `lot-fills` source layer
      // covers all NYC lots, so we filter on bbl via the store's allowed set.
      tab_map.ps.on_click(function(click_values){
        if (!selection_store) { return; }
        let feature = click_values && click_values.feature;
        if (!feature || !feature.properties) { return; }
        if (click_values.layer !== 'lot-fills') { return; }
        let clicked_bbl = feature.properties.bbl;
        if (clicked_bbl == null) { return; }
        if (!selection_store.isAllowed(clicked_bbl)) {
          debug_log('click ignored: bbl not on current block', clicked_bbl);
          return;
        }
        selection_store.toggle(clicked_bbl);
      });

      return tab_map;
    }
  });
}

const api = { mountMap };

if (typeof window !== 'undefined') {
  window.PsBlockAnalyzer = api;
}

export default api;
